import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatInDoc, ChatInDocWorkspace } from '../../contexts/ChatInDocContext';
import { useMenuPermissions } from '../../hooks/useMenuPermissions';
import { useToast } from '../UI/ToastProvider';
import { workspaceService } from '../../services/workspaceService';
import { WorkspaceAvatar } from '../UI/WorkspaceAvatar';
import {
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  DocumentMagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface ChatSessionsListProps {
  onShowDocumentSearch: () => void;
  agentSwitchDisabled?: boolean;
}

const AVATAR_HUES = [210, 145, 280, 35, 175];

function hashHue(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_HUES[Math.abs(hash) % AVATAR_HUES.length];
}

function formatAgentLabel(w: ChatInDocWorkspace): string {
  const a = (w.assistantName || '').trim();
  const n = (w.name || '').trim();
  if (a && n && a !== n) {
    return `${a} - ${n}`;
  }
  return a || n || 'Agente';
}

function initialsFromLabel(label: string): string {
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, 2);
  }
  return label.slice(0, 2).toUpperCase() || '?';
}

export const ChatSessionsList: React.FC<ChatSessionsListProps> = ({
  onShowDocumentSearch,
  agentSwitchDisabled = false,
}) => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { isAdmin } = useMenuPermissions();
  const {
    threads,
    activeThreadId,
    setActiveThread,
    addThread,
    removeThread,
    isBackendConnected,
    workspace,
    isLoadingWorkspace,
    chatWorkspaces,
    activeWorkspaceId,
    selectWorkspace,
    refreshChatWorkspaces,
  } = useChatInDoc();

  const [searchQuery, setSearchQuery] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [agentsExpanded, setAgentsExpanded] = useState(true);

  const filteredThreads = threads.filter((thread) =>
    thread.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const assistantShortName = workspace?.assistantName?.trim() || 'Agente';
  const conversationHeader = `Conversas de ${assistantShortName} (${threads.length})`;

  const handleNewThread = async () => {
    try {
      await addThread();
    } catch (error) {
      console.error('Erro ao criar nova thread:', error);
    }
  };

  const handleThreadSelect = (threadId: string) => {
    void setActiveThread(threadId);
  };

  const handleDeleteThread = async (threadId: string) => {
    try {
      await removeThread(threadId);
    } catch (error) {
      console.error('Erro ao remover thread:', error);
    }
  };

  const handleSelectAgent = (workspaceId: string) => {
    if (!workspaceId || workspaceId === activeWorkspaceId) {
      return;
    }
    if (agentSwitchDisabled) {
      addToast('Aguarde o fim da resposta para trocar de agente.', 'info');
      return;
    }
    void selectWorkspace(workspaceId);
  };

  const handleNewAgent = () => {
    navigate('/chatbot-admin/workspaces');
  };

  const handleConfigureAgent = (e: React.MouseEvent, workspaceId: string) => {
    e.stopPropagation();
    navigate(`/chatbot-admin/workspaces/edit?id=${workspaceId}`);
  };

  const handleDeleteAgent = async (e: React.MouseEvent, ws: ChatInDocWorkspace) => {
    e.stopPropagation();
    if (agentSwitchDisabled) {
      addToast('Aguarde o fim da resposta antes de excluir um agente.', 'info');
      return;
    }
    const label = formatAgentLabel(ws);
    if (
      !window.confirm(
        `Excluir o agente "${label}"? As conversas vinculadas poderão ser afetadas. Esta ação não pode ser desfeita aqui.`
      )
    ) {
      return;
    }
    try {
      await workspaceService.deleteWorkspace(ws.id);
      addToast('Agente excluído.', 'success');
      await refreshChatWorkspaces();
    } catch {
      addToast('Não foi possível excluir o agente.', 'error');
    }
  };

  const agentRows = useMemo(
    () =>
      chatWorkspaces.map((w) => ({
        w,
        label: formatAgentLabel(w),
        hue: hashHue(w.id),
      })),
    [chatWorkspaces]
  );

  if (isCollapsed) {
    return (
      <div className="w-16 bg-white border-r border-gray-200 flex flex-col items-center py-4">
        <button
          type="button"
          onClick={() => setIsCollapsed(false)}
          className="p-2 text-gray-600 hover:text-blue-600 mb-4"
          title="Expandir"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={handleNewThread}
          className="p-2 text-gray-600 hover:text-blue-600 mb-4"
          disabled={!isBackendConnected || !workspace}
          title="Nova conversa"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={onShowDocumentSearch}
          className="p-2 text-gray-600 hover:text-blue-600"
          title="Buscar documentos"
        >
          <DocumentMagnifyingGlassIcon className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="w-80 bg-white border-r border-gray-200 flex flex-col min-h-0">
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Copiloto</h2>
          <button
            type="button"
            onClick={() => setIsCollapsed(true)}
            className="p-1 text-gray-600 hover:text-blue-600"
            title="Recolher"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden mb-3">
          <div className="flex items-center bg-gray-50 hover:bg-gray-100">
            <button
              type="button"
              onClick={() => setAgentsExpanded((v) => !v)}
              className="flex-1 flex items-center gap-1 px-3 py-2 text-left min-w-0"
            >
              <span className="text-sm font-medium text-gray-800 truncate">
                Agentes ({chatWorkspaces.length})
              </span>
              <ChevronDownIcon
                className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform ${agentsExpanded ? '' : '-rotate-90'}`}
              />
            </button>
            <button
              type="button"
              onClick={handleNewAgent}
              className="p-2 mr-1 rounded text-blue-600 hover:bg-blue-100 flex-shrink-0"
              title="Novo agente"
            >
              <PlusIcon className="h-5 w-5" />
            </button>
          </div>

          {agentsExpanded && (
            <ul className="divide-y divide-gray-100 max-h-56 overflow-y-auto">
              {agentRows.length === 0 ? (
                <li className="px-3 py-2 text-xs text-gray-500">Nenhum agente disponível</li>
              ) : (
                agentRows.map(({ w, label, hue }) => {
                  const selected = w.id === activeWorkspaceId;
                  const activeStatus = w.isActive !== false;
                  return (
                    <li key={w.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => handleSelectAgent(w.id)}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault();
                            handleSelectAgent(w.id);
                          }
                        }}
                        className={`flex items-start gap-2 px-2 py-2.5 cursor-pointer transition-colors ${
                          selected ? 'bg-slate-200/80' : 'hover:bg-gray-50'
                        }`}
                      >
                        <WorkspaceAvatar
                          workspaceId={w.id}
                          avatar={w.avatar}
                          alt={label}
                          className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                          imgClassName="flex-shrink-0 w-9 h-9 rounded-full object-cover"
                          fallbackStyle={{ backgroundColor: `hsl(${hue} 48% 42%)` }}
                        >
                          {initialsFromLabel(label)}
                        </WorkspaceAvatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate" title={label}>
                            {label}
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span
                              className={`inline-block w-2 h-2 rounded-full ${
                                activeStatus ? 'bg-emerald-500' : 'bg-amber-400'
                              }`}
                            />
                            <span className="text-[11px] text-gray-500">
                              {activeStatus ? 'Ativo' : 'Inativo'}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-0.5 flex-shrink-0">
                          <button
                            type="button"
                            onClick={(e) => handleConfigureAgent(e, w.id)}
                            className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200/80"
                            title="Configurar agente"
                          >
                            <Cog6ToothIcon className="h-4 w-4" />
                          </button>
                          {isAdmin() ? (
                            <button
                              type="button"
                              onClick={(e) => void handleDeleteAgent(e, w)}
                              className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                              title="Excluir agente"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                })
              )}
            </ul>
          )}
        </div>

        <p className="text-xs font-medium text-gray-700 mb-2">{conversationHeader}</p>

        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar conversas por título..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <button
          type="button"
          onClick={() => void handleNewThread()}
          disabled={!isBackendConnected || !workspace || isLoadingWorkspace}
          className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          {isLoadingWorkspace ? 'Carregando...' : 'Nova Conversa'}
        </button>

        <button
          type="button"
          onClick={onShowDocumentSearch}
          className="mt-2 w-full flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors hidden"
        >
          <DocumentMagnifyingGlassIcon className="h-4 w-4 mr-2" />
          Buscar Documentos
        </button>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {filteredThreads.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            {searchQuery ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa ainda'}
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filteredThreads.map((thread) => (
              <div
                key={thread.id}
                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                  thread.id === activeThreadId
                    ? 'bg-blue-100 border border-blue-200'
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => handleThreadSelect(thread.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                      <h3 className="font-medium text-gray-900 text-sm truncate">{thread.title}</h3>
                    </div>

                    {thread.messages.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        {thread.messages[thread.messages.length - 1].content}
                      </p>
                    )}

                    <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                      <span>
                        {thread.messages.length} mensage{thread.messages.length !== 1 ? 'ns' : 'm'}
                      </span>
                      <span>{thread.updatedAt.toLocaleDateString('pt-BR')}</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDeleteThread(thread.id);
                    }}
                    className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                    title="Excluir conversa"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
